import React, { useState } from "react";
import { Lock, X, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/contexts/LanguageContext";

const API_FALLBACK = "https://viaggio-test-production.up.railway.app";

export default function ChangePasswordModal({ isOpen, onClose }) {
  const { lang } = useLang();
  const isEn = lang === "en";

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword.length < 6) {
      setError(
        isEn
          ? "The new password must be at least 6 characters long."
          : "La nuova password deve contenere almeno 6 caratteri."
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(
        isEn
          ? "New passwords do not match."
          : "Le nuove password non coincidono."
      );
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("genio_token");
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      if (api && typeof api.post === "function") {
        await api.post("/auth/change-password", {
          old_password: oldPassword,
          new_password: newPassword
        });
      } else {
        const res = await fetch(`${API_FALLBACK}/api/auth/change-password`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            old_password: oldPassword,
            new_password: newPassword
          })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.detail || (isEn ? "Failed to update password" : "Errore durante l'aggiornamento"));
        }
      }

      toast.success(
        isEn
          ? "Password updated successfully!"
          : "Password aggiornata con successo!"
      );
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      onClose();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        (isEn ? "Incorrect current password" : "Password attuale errata o sessione scaduta");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-[#0B1437] border border-[#D4AF37]/40 rounded-2xl p-6 md:p-8 shadow-2xl text-white">
        
        {/* Pulsante Chiusura */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition p-1"
          aria-label="Chiudi"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Intestazione */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F1D26B] via-[#D4AF37] to-[#7A5A18] flex items-center justify-center text-[#0B1437] shadow">
            <KeyRound className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-serif text-xl font-bold text-[#EDE7D8]">
              {isEn ? "Change Password" : "Cambia Password"}
            </h3>
            <p className="text-xs text-gray-400">
              {isEn
                ? "Enter your current password and choose a new one."
                : "Inserisci la tua password attuale e scegline una nuova."}
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 text-xs text-red-400 bg-red-950/50 border border-red-900/60 p-3 rounded-xl text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-1">
              {isEn ? "Current Password" : "Password Attuale"}
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-3.5" />
              <input
                type="password"
                required
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-[#12204f] border border-gray-700 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37]"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-1">
              {isEn ? "New Password" : "Nuova Password (min. 6 caratteri)"}
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-3.5" />
              <input
                type="password"
                required
                minLength={6}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-[#12204f] border border-gray-700 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37]"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-1">
              {isEn ? "Confirm New Password" : "Conferma Nuova Password"}
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-3.5" />
              <input
                type="password"
                required
                minLength={6}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-[#12204f] border border-gray-700 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37]"
              />
            </div>
          </div>

          <div className="pt-2 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="w-1/2 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl text-sm transition font-medium"
            >
              {isEn ? "Cancel" : "Annulla"}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-1/2 py-2.5 bg-[#D4AF37] hover:bg-[#b8962d] text-[#0B1437] font-bold rounded-xl text-sm transition shadow disabled:opacity-50"
            >
              {loading
                ? (isEn ? "Saving..." : "Salvataggio...")
                : (isEn ? "Update" : "Aggiorna")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
